import React, { useState } from 'react';
import { JourneyNavigationHandler, Routes } from '../../../routes';
import { ButtonType, ButtonWidget } from '../../../widgets/buttons/ButtonWidget';
import { CenterFormWidget } from '../../../widgets/layout/CenterFormWidget';
import { styled } from '../../../theme';
import { CardManagementFormWidget } from '../manage-subscriptions/widgets/billing/CardManagementFormWidget';
import { observer } from 'mobx-react';
import { useSystem } from '../../../hooks/useSystem';

export interface CardDetailsDialogProps {
  handler: JourneyNavigationHandler<Routes.CARD_SETTINGS>;
}

export const CardDetailsDialog: React.FC<CardDetailsDialogProps> = observer(({ handler }) => {
  const { org_id } = handler.params;
  const system = useSystem();
  const [loading, setLoading] = useState(false);
  return (
    <S.Container
      loading={loading}
      primaryLabel="Manage cards"
      secondaryLabel="Add or remove cards on file."
      noLogo={true}
    >
      <CardManagementFormWidget
        back={{
          label: 'Cancel',
          action: () => {
            handler.navigate(Routes.BILLING, { ...handler.params });
          }
        }}
        org_id={org_id}
        cardAdded={async (card) => {
          const update = await system.dialogStore.confirmDialog({
            title: 'Update subscriptions?',
            message: `Click 'Confirm' to assign this new card to all active subscriptions.`
          });
          if (update) {
            setLoading(true);
            const plans = await system.orgStore.org(org_id).loadPlans();
            for (let plan of plans) {
              if (plan.model.billing_enabled) {
                await plan.setPaymentConfiguration(card);
              }
            }
            setLoading(false);
          }
        }}
        generateCard={(card, element) => {
          return (
            <S.Card>
              {element}
              <S.Button
                type={ButtonType.SECONDARY}
                disabled={card.model.subscriptions.length > 0}
                tooltip={card.model.subscriptions.length > 0 ? 'Card is in use by active subscriptions' : undefined}
                label="Remove"
                action={async () => {
                  const confirm = await system.dialogStore.confirmDialog({
                    title: 'Are you sure?',
                    message: 'This operation cannot be undone.'
                  });
                  if (!confirm) {
                    return;
                  }
                  setLoading(true);
                  await card.delete();
                  setLoading(false);
                }}
              />
            </S.Card>
          );
        }}
      />
    </S.Container>
  );
});

namespace S {
  export const Container = styled(CenterFormWidget)`
    max-width: 600px;
    width: 600px;
  `;

  export const Card = styled.div`
    cursor: pointer;
    display: flex;
    align-items: center;
    flex-direction: column;
  `;

  export const Button = styled(ButtonWidget)`
    margin-top: 5px;
  `;
}
